import { PNPG_AREA_ID } from "../data/pnpgProtectionRules.js";
import { loadProtectedZonesCache, saveProtectedZonesCache } from "./protectedZonesCache.js";
import { assessRouteInPnpg, validateProtectedZonesGeoJson } from "./protectedZones.js";

const appBaseUrl = import.meta.env?.BASE_URL || "/";
const normalizedAppBaseUrl = appBaseUrl.endsWith("/") ? appBaseUrl : `${appBaseUrl}/`;

export const PNPG_ZONES_BUNDLED_DATA_URL = `${normalizedAppBaseUrl}data/pnpg-protection-zones.geojson`;
export const PNPG_ZONES_IOS_DATA_URL = "trekkin-map://bundle/pnpg-protection-zones.geojson";
export const PNPG_RULES_VERSION = "2026-10-24.1";

let inFlightLoad = null;

export function resolvePnpgZonesDataUrl() {
  const isIosNative =
    typeof window !== "undefined" &&
    typeof window?.webkit?.messageHandlers?.nativeBridge?.postMessage === "function";
  return isIosNative ? PNPG_ZONES_IOS_DATA_URL : PNPG_ZONES_BUNDLED_DATA_URL;
}

function assertValidDataset(geoJson) {
  const validation = validateProtectedZonesGeoJson(geoJson);
  if (!validation.valid || !geoJson?.features?.length) {
    throw new Error(`protected_zones_dataset_invalid: ${validation.errors.join(" ")}`);
  }
  return geoJson;
}

async function fetchBundledDataset() {
  // O carregamento é partilhado pela camada, análise do percurso e GPS. Não
  // associamos o fetch ao AbortSignal de um único consumidor, para que fechar
  // um painel não cancele o mesmo pedido para os restantes consumidores.
  const dataUrl = resolvePnpgZonesDataUrl();
  const response = await fetch(dataUrl, { cache: "no-cache" });
  if (!response.ok) throw new Error(`protected_zones_http_${response.status}`);
  return assertValidDataset(await response.json());
}

export async function loadPnpgProtectedZones({
  preferCache = true,
  refresh = true,
  signal,
} = {}) {
  if (inFlightLoad) return inFlightLoad;
  inFlightLoad = (async () => {
    const cached = preferCache ? await loadProtectedZonesCache(PNPG_AREA_ID) : null;
    if (cached?.geoJson && (!refresh || globalThis.navigator?.onLine === false)) {
      return {
        geoJson: assertValidDataset(cached.geoJson),
        cachedAt: cached.cachedAt || null,
        source: "cache",
      };
    }
    try {
      const geoJson = await fetchBundledDataset();
      // Persisting 3+ MB is an optimisation, not a requirement for displaying
      // the layer. Some WKWebView storage configurations reject IndexedDB (and
      // localStorage is too small), so a cache error must never discard the
      // dataset that was already loaded successfully.
      try {
        const saved = await saveProtectedZonesCache({
          areaId: PNPG_AREA_ID,
          geoJson,
          rulesVersion: PNPG_RULES_VERSION,
          sourceUpdatedAt: geoJson?.metadata?.generatedAt || null,
        });
        return { geoJson, cachedAt: saved.cachedAt, source: "bundled" };
      } catch (cacheError) {
        return {
          geoJson,
          cachedAt: null,
          source: "bundled_uncached",
          cacheError,
        };
      }
    } catch (error) {
      if (cached?.geoJson) {
        return {
          geoJson: assertValidDataset(cached.geoJson),
          cachedAt: cached.cachedAt || null,
          source: "cache_fallback",
          refreshError: error,
        };
      }
      throw error;
    }
  })();
  try {
    return await inFlightLoad;
  } finally {
    inFlightLoad = null;
  }
}

export async function assessPnpgRoute({
  route,
  activity,
  participantCount = 1,
  organized = false,
  safetyMarginMeters = 25,
  signal,
} = {}) {
  const loaded = await loadPnpgProtectedZones({ signal });
  return {
    ...assessRouteInPnpg({
      route,
      geoJson: loaded.geoJson,
      activity,
      participantCount,
      organized,
      safetyMarginMeters,
    }),
    datasetSource: loaded.source,
    cachedAt: loaded.cachedAt,
  };
}

